import type { ClientType, IdentityClientView, IdentityRequestView, IdentityView } from '~/types/identity'
import { apiErrorStatus, type CompanyView } from '~/utils/client-invoice'

/** An account without a client orders like a guest: the invoice choice is per order. */
export function isLinkedIdentity(view: IdentityView | null | undefined): view is IdentityView {
  return !!view && view.state !== 'no_client'
}

export function identityClients(view: IdentityView | null | undefined): IdentityClientView[] {
  if (!view) return []
  return [view.active, view.reserved].filter((client): client is IdentityClientView => !!client)
}

/** Active first; null when the account has no client of this type. */
export function clientOfType(view: IdentityView | null | undefined, type: ClientType): IdentityClientView | null {
  return identityClients(view).find(client => client.type === type) ?? null
}

export function pendingRequest(view: IdentityView | null | undefined): IdentityRequestView | null {
  return view?.request?.status === 'PENDING' ? view.request : null
}

/** Name and EIK for pending-request copy, e.g. "Глоуб Систем ООД | 205641766". */
export function pendingCompanyLabel(request: IdentityRequestView | null | undefined): string {
  if (!request) return ''
  const name = request.companyName?.trim()
  return name ? `${name} | ${request.eik}` : request.eik
}

export function rejectedRequest(view: IdentityView | null | undefined): IdentityRequestView | null {
  return view?.request?.status === 'REJECTED' ? view.request : null
}

/** The API writes "-" when the register has no office address (NeoSoft ignores empty values). */
function presentAddress(raw: string | null | undefined): string | null {
  const address = raw?.trim()
  return address && address !== '-' ? address : null
}

export function companyFromIdentityClient(client: IdentityClientView | null | undefined): CompanyView | null {
  if (!client || client.type !== 'company' || !client.eik) return null
  return {
    name: client.name.trim(),
    uic: client.eik,
    vat: client.vatNumber?.trim() || null,
    city: null,
    address: presentAddress(client.admAddress),
  }
}

/** A linked order is for the active client; with asRequest it waits for the requested company. */
export function identityWantsInvoice(view: IdentityView | null | undefined, asRequest: boolean): boolean {
  if (view?.active?.type === 'company') return true
  if (clientOfType(view, 'company')) return false
  return asRequest && !!pendingRequest(view)
}

/** Order as the pending company only when the account has no company of its own. */
export function ordersAsPendingRequest(view: IdentityView | null | undefined, asRequest: boolean): boolean {
  return asRequest && !!pendingRequest(view) && !clientOfType(view, 'company')
}

export type CheckoutInvoiceNotice = 'hidden' | 'pending_request' | 'own_company_and_pending'

/** Checkout copy for a pending company request; the profile always shows the request. */
export function checkoutInvoiceNotice(
  view: IdentityView | null | undefined,
  context: 'checkout' | 'profile',
): CheckoutInvoiceNotice {
  if (!pendingRequest(view)) return 'hidden'
  if (context === 'profile') return 'pending_request'
  if (view?.active?.type === 'company') return 'own_company_and_pending'
  if (clientOfType(view, 'company')) return 'hidden'
  return 'pending_request'
}

/** Name and EIK of the active company, same shape as pendingCompanyLabel. */
export function activeCompanyLabel(view: IdentityView | null | undefined): string {
  const active = view?.active
  if (active?.type !== 'company') return ''
  const name = active.name?.trim()
  return name && active.eik ? `${name} | ${active.eik}` : (name || active.eik || '')
}

export type IdentityErrorCode =
  | 'no_client'
  | 'identity_review'
  | 'client_inactive'
  | 'account_phone_invalid'
  | 'invalid'
  | 'not_found'
  | 'request_pending'
  | 'lookup_unavailable'
  | 'neosoft'
  | 'failed'

const KNOWN_CODES = new Set<IdentityErrorCode>([
  'no_client',
  'identity_review',
  'client_inactive',
  'account_phone_invalid',
  'invalid',
  'not_found',
  'request_pending',
  'lookup_unavailable',
])

/** The `detail.errors[0].code` of POST /security/identity, else a code by HTTP status. */
export function identityErrorCode(error: unknown): IdentityErrorCode {
  const detail = (error as { data?: { detail?: unknown } } | null)?.data?.detail
  const errors = (detail as { errors?: { code?: string }[] } | null)?.errors
  const code = Array.isArray(errors) ? errors[0]?.code : undefined
  if (code && KNOWN_CODES.has(code as IdentityErrorCode)) return code as IdentityErrorCode
  const status = apiErrorStatus(error)
  if (status === 503) return 'lookup_unavailable'
  if (status === 502) return 'neosoft'
  return 'failed'
}
